/**
 * 複数選択フィルター(v3.7)。
 * ボタンを押すとチェックボックスのリストが開き、複数選んで絞り込める。
 * 選択なし = すべて表示。選択中はボタンに件数を表示する。
 */
import { useEffect, useRef, useState } from 'react';

export type FilterOption = { value: string; label: string };

export default function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  }

  const active = selected.length > 0;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          // 高さはツールバーのselect(.toolbar select)と揃える(2026-08-28)
          height: 30,
          padding: '0 12px',
          fontSize: 12.5,
          borderRadius: 6,
          background: active ? 'var(--blue-bg)' : '#fff',
          border: `1px solid ${active ? 'var(--brand-blue)' : 'var(--line)'}`,
          color: active ? 'var(--brand-blue)' : 'var(--ink)',
          fontWeight: active ? 600 : 400,
        }}
      >
        {label}
        {active ? `(${selected.length})` : ''} ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(0, 0, 0, .14)',
            padding: '6px 0',
            minWidth: 190,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          <button
            onClick={() => onChange([])}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '7px 12px',
              background: 'none',
              fontSize: 12.5,
              color: active ? 'var(--brand-blue)' : 'var(--muted)',
              fontWeight: 600,
            }}
          >
            すべて(選択を解除)
          </button>
          {options.map((o) => (
            <label
              key={o.value}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}
            >
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
